import Vue from 'vue'
import { setStorage } from "_lib/utils/utils";
import { RTC_AV_LINE_LIST_DEFAULT, RTC_MIC_MULT_POWER_DEFAULT } from "_lib/constants/rtc"
import { DEFAULT_ROOM_COLOR_TYPE } from "_lib/constants/linear-color"
import { hexToHSV, hsvToHex, getColorsTheme } from "_lib/utils/utilsColor"
import { randomNumByArray } from "_lib/utils/utilsArray"
import { DEFAULT_TIP_SOUND_CONTROL_VALUE, GENERATE_DEFAULT_SETTING_DATA } from "_lib/constants/config"
import { getBasicRoomData } from '_lib/utils/utilsRoom';

let requireRoomListCb = null

const Settings = {
  state: () => ({
    user_account_settings: {},
    user_volumes: {},
    // 持久化设置储存，web存在localStorage，客户端存在本地json，非持久化config不要放在setting_data里(如接口获取的账号设置)
    setting_data: GENERATE_DEFAULT_SETTING_DATA(),
    old_ws_id: '', // 被覆盖的ws_id，
    ws_id: '', // ws唯一id标识
    version_info: {}, //客户端版本 {client_version: 客户端版本，asar_version: electron版本(对应asar安装包版本, edit_authority: 文件夹是否允许编辑)}
    client_download_url: '', // 语音客户端下载链接
    act_list: [], // 活动列表
    has_new_version: false, // 客户端是否有新版本
    has_asar_downloaded: false, // asar下载完成
    /**
      - /chatroom/v1/account/game_state
      - /chatroom/friend/get_user_friends_list
      - /chatroom/room/view
      - /chatroom/v2/room/users
      - /chatroom/report/video_time_report
    */
    api_poll_interval: [], // 接口轮询间隔
    window_show_state: true,
    global_config: {}, // 后端控制的开关对象
    system_theme: 'light', // 系统的明暗主题 ['light', 'dark']
    laughter_flatten_list: [], // 语音包数据经过flat操作之后的数组
    obs_auth: undefined, // OBS内覆盖授权状态
    laughter_sort_data: {}, // 语音包调整顺序的数据
    voice_packs_map: {}, // 语音包房间和数据的映射
    voice_room_list: [], // 语音包房间列表
    user_card_setting: {}, // 开黑名片列表
    is_init_laughter_tracks: false, // 是否已经初始化语音包列表 1.39动态加载
    device_id: undefined, // windows 机器码
  }),
  getters: {
    user_account_settings: (state) => state.user_account_settings,
    voice_pack_volume: (state) => state.user_account_settings.voice_pack_volume,
    anchor_mode: (state) => state.user_account_settings.anchor_mode,
    anchor_mode_hide_invite: (state) => state.user_account_settings.anchor_mode_hide_invite,
    auto_update_asar: (state) => state.user_account_settings.auto_update_asar,
    user_volumes: (state) => state.user_volumes,
    setting_data: (state) => state.setting_data,
    member_voice_level: (state) => state.setting_data.member_voice_level,
    have_accepted_promise: (state) => state.setting_data.have_accepted_promise,
    hide_download_banner: (state) => state.setting_data.hide_download_banner,
    tip_sound_volume: (state) => state.setting_data.tip_sound_volume,
    tip_sound_control: (state) => (k) => {
      if (state.user_account_settings.anchor_mode_mute_audio 
        && state.user_account_settings.anchor_mode) { // 如果开启了主播模式关闭声音，所有提示音关闭
        return 0
      }
      return state.setting_data.tip_sound_control[k] ?? DEFAULT_TIP_SOUND_CONTROL_VALUE[k]
    },
    ws_id: (state) => state.ws_id,
    old_ws_id: (state) => state.old_ws_id,
    version_info: (state) => state.version_info,
    client_download_url: (state) => state.client_download_url,
    act_list: (state) => state.act_list,
    is_act_enable: (state) => (item) => {
      if (!item?.switch) {
        return false
      }
      // 获取当前日期和时间
      const now = new Date().getTime()
      // 设定开始时间和结束时间
      const start = item.act_start_time
      const end = item.act_end_time
      // 判断当前时间是否在时间区间内
      // console.log(now >= start && now <= end);
      if (now >= start && now <= end) {
        return true
      }
      return false
    },
    is_act_intime: (state, getters) => (item) => {
      let cur_os = process.env.IS_CLIENT ? 'client' : 'web'
      return item.display_loc.includes(cur_os) && getters.is_act_enable(item)
    },
    has_new_version: (state) => state.has_new_version,
    has_asar_downloaded: (state) => state.has_asar_downloaded,
    api_poll_interval: (state) => (key, default_num, ignoreWindowState) => {
      if (state.api_poll_interval && state.api_poll_interval[key] && state.api_poll_interval[key].length) {
        const num = randomNumByArray(state.api_poll_interval[key]) || default_num
        return (state.window_show_state || ignoreWindowState) ? num : num * 4
      }
      return default_num
    },
    window_show_state: (state) => state.window_show_state,
    global_config: (state) => state.global_config,
    global_switches: (state) => state.global_config?.switches || {},
    global_av_line_list: (state) => state.global_config.av_line_list || RTC_AV_LINE_LIST_DEFAULT,
    global_mic_mult_power: (state) => {
      return {
        ...RTC_MIC_MULT_POWER_DEFAULT,
        ...state.global_config.mic_mult_power || {},
      }
    },
    global_check_bilibili: (state) => state.global_config?.switches?.check_bilibili,
    system_theme: (state) => state.system_theme,
    page_theme: (state, getters) => {
      let type = 'dark', theme_data = getters.show_theme_data
      if (DEFAULT_ROOM_COLOR_TYPE[theme_data.main_color_v2] === 'DARK') {
        type = 'dark'
      } else if (DEFAULT_ROOM_COLOR_TYPE[theme_data.main_color_v2] === 'LIGHT') {
        type = 'light'
      } else if (DEFAULT_ROOM_COLOR_TYPE[theme_data.main_color_v2] === 'FOLLOW' || theme_data.main_color_v2 === null) {
        if (theme_data.bg_pic_main_color || theme_data.bg_color?.length) {
          type = getColorsTheme(theme_data.bg_pic_main_color ? [theme_data.bg_pic_main_color] : (theme_data.bg_color || []))
        } else {
          type = getters.system_theme
        }
      }
      return type
    },
    bar_theme: (state, getters) => {
      let type = 'dark', theme_data = getters.show_theme_data
      if (DEFAULT_ROOM_COLOR_TYPE[theme_data.bar_main_color_v2] === 'DARK') {
        type = 'dark'
      } else if (DEFAULT_ROOM_COLOR_TYPE[theme_data.bar_main_color_v2] === 'LIGHT') {
        type = 'light'
      } else if (DEFAULT_ROOM_COLOR_TYPE[theme_data.bar_main_color_v2] === 'FOLLOW') {
        if (theme_data.bg_pic_main_color || theme_data.bg_color?.length) {
          type = getColorsTheme(theme_data.bg_pic_main_color ? [theme_data.bg_pic_main_color] : (theme_data.bg_color || []))
        } else {
          type = getters.system_theme
        }
      }
      return type
    },
    laughter_flatten_list: (state) => state.laughter_flatten_list,
    get_member_voice_level: (state, rootState) => (user_id) => {
      let anchor_user = rootState.anchor_mute_user_ids[user_id]
      return state.user_account_settings.anchor_mode && anchor_user && !anchor_user.ignore ? 0 // 如果在主播模式静音名单里并且没被忽略，音量为0
      : (state.setting_data.member_voice_level[user_id] !== undefined ? state.setting_data.member_voice_level[user_id] : 100)
    },
    // 右侧好友栏，在线离线圆圈边框颜色
    show_theme_sidebar_border_color: (state, getters) => {
      let data = getters.show_theme_data
      if (data.bg_pic && data.bg_pic_main_color) {
        return data.bg_pic_main_color
      } else {
        return data.bg_color && data.bg_color[0]
      }
    },
    // 富文本输入框，语音面板主题颜色
    emphasize_show_theme_bg_color: (state, getters) => {
      let color
      if (getters.show_theme_data.bg_pic && getters.show_theme_data.bg_pic_main_color) {
        color = getters.show_theme_data.bg_pic_main_color
      } else if (getters.show_theme_data.bg_color) {
        const bg_color = getters.show_theme_data.bg_color
        if (JSON.stringify(bg_color) == '["#E6EAF2","#FFFFFF"]') {
          return '#f0f2f5'
        } else if (JSON.stringify(bg_color) == '["#1F2225","#36393E"]') {
          return '#1f2225'
        }

        color = getters.show_theme_data.bg_color[1]
      } else {
        color = ''
      }
      if (color) {
        const hsv = hexToHSV(color)
        let { v } = hsv
        if (hsv.s >= 5) {
          hsv.s += 10
        }

        let theme_data = getters.show_theme_data
        let type = getColorsTheme(theme_data.bg_pic_main_color ? [theme_data.bg_pic_main_color] : (theme_data.bar_color || []))
        if (type === 'light') {
          if (v <= 90) {
            v += 10;
          } else {
            v -= 5;
          }
        } else if (type === 'dark') {
          if (v >= 10) {
            v -= 10;
          } else {
            v += 5;
          }
        }
        hsv.v = v
        return hsvToHex(hsv)
      }
    },
    get_has_exist_key: (state) => (key, judge_values) => {
      if (!key || !judge_values || !judge_values.length) {
        throw new Error('get_has_exist_key 参数错误')
      }
      // 存放vuex里所有快捷键设置的key
      let all_keys_list = [
        'audio_press_key', 'mic_switch_key', 'voice_switch_key', 
        'screenshot_key', 'continuous_key_list', 'laughter_key_map'
      ]
      // 排除掉判断的key
      all_keys_list = all_keys_list.filter(item => item !== key)
      let config = state.setting_data;
      // 存放所有已经设置的快捷键（列表）
      // 例如：[[1, 2], [3]]，就代表已经设置了快捷键1+2和3
      const keys = []
      // 遍历需要判断的快捷键设置，将所有已经设置的快捷键放入keys
      all_keys_list.forEach(item => {
        const key = config[item]
        if (item === 'continuous_key_list') {
          keys.push(...config.map(ck => ck.trigger_key))
        } else if (item === 'laughter_key_map') {
          const laughter_keys = Object.values(key)
          keys.push(...laughter_keys)
        } else if (Array.isArray(key) && key.length > 0) {
          keys.push(key)
        } else if (typeof key === 'number' || typeof key === 'string') {
          keys.push([Number(key)])
        }
      })
      // 使用排序过后的字符串判断keys的子集有无和judge_values完全相等的，有则代表有重复
      const judge_values_str = judge_values.sort().join(',')
      for (let i = 0; i < keys.length; i++) {
        const key_str = keys[i].sort().join(',')
        if (key_str === judge_values_str) {
          return true
        }
      }
      return false
    },
    obs_auth: (state) => state.obs_auth, // 获取obs_auth
    laughter_sort_data: (state) => state.laughter_sort_data, // 获取语音包排序的数据
    voice_packs_map: (state) => state.voice_packs_map, // 获取语音包数据
    voice_room_list: (state) => state.voice_room_list, // 获取语音房间列表
    user_card_setting: (state) => state.user_card_setting, // 开黑名片列表
    is_init_laughter_tracks: (state) => state.is_init_laughter_tracks, // 是否初始化语音包列表 1.39动态加载
    channel_sidebar_width: (state) => state.setting_data.channel_sidebar_width ?? 280, // 频道侧边栏宽度
    device_id: (state) => state.device_id, // 机器码
  },
  mutations: {
    SET_USER_ACCOUNT_SETTINGS(state, val) {
      state.user_account_settings = {
        ...state.user_account_settings,
        ...val
      }
    },
    SET_ACT_LIST(state, val) {
      val.sort((a,b) => a.priority - b.priority)
      state.act_list = val
    },
    SET_USER_VOLUMES (state, val) {
      state.user_volumes = val
    },
    SET_SETTING_DATA (state, val) {
      state.setting_data = val
    },
    SET_WS_ID(state, val) {
      if (!state.ws_id) {
        state.ws_id = val
        // ws_id更新时需要同步更新electron的ws_id
        if (process.env.IS_CLIENT) {
          window.electronAPI.udpateWsId(val)
        }
      }
    },
    SET_WS_ID_FORCE(state, val) {
      state.ws_id = val
      // ws_id更新时需要同步更新electron的ws_id
      if (process.env.IS_CLIENT) {
        window.electronAPI.udpateWsId(val)
      }
    },
    SET_OLD_WS_ID(state, val) {
      state.old_ws_id = val
    },
    SET_VERSION_INFO(state, val) {
      state.version_info = val
    },
    SET_CLIENT_DOWNLOAD_URL(state, val) {
      state.client_download_url = val
    },
    SET_HAS_NEW_VERSION(state, val) {
      state.has_new_version = val
    },
    SET_HAS_ASAR_DOWNLOADED (state, val) {
      state.has_asar_downloaded = val
    },
    SET_API_POLL_INTERVAL(state, val) {
      state.api_poll_interval = val
    },
    SET_WINDOW_SHOW_STATE(state, val) {
      state.window_show_state = val
    },
    SET_GLOBAL_CONFIG(state, val) {
      state.global_config = {
        ...state.global_config,
        ...val
      }
      // 在electron中储存 global_config
      // 黑神话悟空的上报点位灰度从global_config中取
      if (process.env.IS_CLIENT) {
        setStorage('global_config', state.global_config)
      }
    },
    SET_SYSTEM_THEME (state, val) {
      state.system_theme = val
    },
    SET_LAUGHTER_FLATTEN_LIST(state, val){
      state.laughter_flatten_list = val
    },
    SET_OBS_AUTH(state, val){
      state.obs_auth = val
    },
    SET_LAUGHTER_SORT_DATA(state, val){
      state.laughter_sort_data = val
    },
    SET_VOICE_PACKS_MAP(state, val){
      state.voice_packs_map = val
    },
    SET_VOICE_ROOM_LIST(state, val){
      state.voice_room_list = val
    },
    SET_USER_CARD_SETTING(state, val) {
      state.user_card_setting = val
    },
    SET_DESTINY2_PADSTART(state, val) {
      state.setting_data.destiny2_padstart = val
    },
    SET_IS_INIT_LAUGHTER_TRACKS(state, val) {
      state.is_init_laughter_tracks = val
    },
    SET_DEVICE_ID(state, val) {
      state.device_id = val
    },
  },
  actions: {
    SET_USER_ACCOUNT_SETTINGS({ commit, state, rootState }, val) {
      commit('SET_USER_ACCOUNT_SETTINGS', val)
  
      // 主播模式处理
      if (val.anchor_mode !== undefined && !val.anchor_mode) {
        let _anchor_mute_user_ids = JSON.parse(JSON.stringify(rootState.room.anchor_mute_user_ids))
        rootState.room.anchor_mute_user_ids = {}
        Object.keys(_anchor_mute_user_ids).forEach(user_id => {
          Vue.prototype.$rtc.changeUserVolume(user_id)
        })
      }
      // 如果是客户端，同步用户配置到主进程的store中
      if (process.env.IS_CLIENT) {
        setStorage('user_account_settings', state.user_account_settings)
      }
    },
    SET_SETTING_DATA: ({ commit }, args) => {
      commit('SET_SETTING_DATA', args)
      setStorage('hcsd', args)
    },
    SET_SETTING_DATA_PARAM: ({ commit, state }, args) => {
      let d = {
        ...state.setting_data,
        ...args
      }
      commit('SET_SETTING_DATA', d)
      setStorage('hcsd', d)
    },
    SET_MEMBER_VOICE_LEVEL: ({ commit, state }, args) => {
      let member_voice_level = {
        ...state.setting_data.member_voice_level,
        ...args
      }
      let d = {
        ...state.setting_data,
        member_voice_level
      }
      commit('SET_SETTING_DATA', d)
      setStorage('hcsd', d)
    },
    SET_OBS_AUTH({ commit, rootState }, args){
      // 开启obs覆盖时，监听obs请求的房间列表ipc
      if (process.env.IS_CLIENT) {
        if (args) {
          if (!requireRoomListCb) {
            requireRoomListCb = () => {
              window.obsOverlayAPI.updateRoomList(getBasicRoomData(rootState.room.room_list))
            }
            window.obsOverlayAPI.onRequireRoomList(requireRoomListCb)
          }
        } else {
          window.obsOverlayAPI.offRequireRoomList(requireRoomListCb)
          requireRoomListCb = null
        }
      }
      commit('SET_OBS_AUTH', args)
    },
  }
}
export default Settings